/**
 * GitHub-PR-style unified diff between two structured rulebooks.
 *
 * The two rulebooks (v1 frozen, v2 current) share the `WCRulebook`
 * shape — sections keyed by `id`, each with `kind` ∈ rows/bullets/
 * rounds/superpowers. We flatten each section into stable line text
 * ("label: value", bullet, round descriptor, superpower card), run
 * jsdiff line-level comparison, and render hunks with red `−` /
 * green `+` markers plus a section-status chip.
 *
 * Cosmetic re-tagging: after the raw line diff, paired removed/added
 * lines whose numeric/value tokens match (so the rule is unchanged,
 * only the wording differs) get retagged as "cosmetic" and rendered
 * yellow with a `~` marker — distinct from real semantic changes.
 *
 * Single component, single column. Each section is its own block so
 * unchanged sections collapse cleanly while changed sections expand
 * the relevant hunks. Designed to read at a glance the way a PR diff
 * does — admin scans the chips down the left, drills into the
 * red/green/yellow lines on the right.
 */

import { useMemo, useState } from 'react';

import { diffLines } from 'diff';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import type { WCRulebook, WCRulebookSection } from './api';

export function RulebookDiff({ v1, v2 }: { v1: WCRulebook; v2: WCRulebook }) {
  const sectionDiffs = useMemo(() => buildSectionDiffs(v1, v2), [v1, v2]);
  const stats = useMemo(() => summarise(sectionDiffs), [sectionDiffs]);

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-card px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-sm font-medium">
              Rulebook diff — {v1.version} → {v2.version}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {stats.total} sections · {stats.same} unchanged · {stats.cosmetic} cosmetic ·{' '}
              {stats.changed} changed · {stats.added} added · {stats.removed} removed ·{' '}
              {stats.linesAdded}+ / {stats.linesRemoved}− / {stats.linesCosmetic}~
            </div>
          </div>
          <div className="flex gap-2 text-xs">
            <Legend tone="added" label="Added" />
            <Legend tone="removed" label="Removed" />
            <Legend tone="cosmetic" label="Cosmetic" />
            <Legend tone="same" label="Unchanged" />
          </div>
        </div>
      </div>

      {sectionDiffs.map((sd) => (
        <SectionDiffCard key={sd.id} diff={sd} />
      ))}
    </div>
  );
}

// ─── Legend chip ─────────────────────────────────────────────────────

function Legend({
  tone,
  label,
}: {
  tone: 'added' | 'removed' | 'same' | 'cosmetic';
  label: string;
}) {
  const swatch =
    tone === 'added'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : tone === 'removed'
        ? 'bg-red-500/15 text-red-700 dark:text-red-300'
        : tone === 'cosmetic'
          ? 'bg-yellow-400/20 text-yellow-700 dark:text-yellow-300'
          : 'bg-muted text-muted-foreground';
  return <span className={cn('rounded-full px-2 py-0.5', swatch)}>{label}</span>;
}

// ─── Status chip per section ────────────────────────────────────────

type SectionStatus = 'same' | 'cosmetic' | 'changed' | 'added' | 'removed';

function StatusChip({ status }: { status: SectionStatus }) {
  const map: Record<SectionStatus, { label: string; klass: string }> = {
    same: { label: 'Unchanged', klass: 'bg-muted text-muted-foreground' },
    cosmetic: {
      label: 'Cosmetic only',
      klass: 'bg-yellow-400/20 text-yellow-700 dark:text-yellow-300',
    },
    changed: {
      label: 'Changed',
      klass: 'bg-orange-500/20 text-orange-700 dark:text-orange-300',
    },
    added: { label: 'New in v2', klass: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
    removed: { label: 'Removed in v2', klass: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  };
  const m = map[status];
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs uppercase tracking-wide', m.klass)}>
      {m.label}
    </span>
  );
}

// ─── Section card ────────────────────────────────────────────────────

function SectionDiffCard({ diff }: { diff: SectionDiff }) {
  const [expanded, setExpanded] = useState(diff.status !== 'same');
  const hasChanges = diff.status !== 'same';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{diff.heading}</CardTitle>
        <div className="flex items-center gap-2">
          <StatusChip status={diff.status} />
          {hasChanges ? null : (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {expanded ? 'Hide' : 'Show'}
            </button>
          )}
        </div>
      </CardHeader>
      {expanded ? (
        <CardContent>
          <DiffHunks lines={diff.lines} />
        </CardContent>
      ) : null}
    </Card>
  );
}

// ─── Diff hunk renderer ─────────────────────────────────────────────

function DiffHunks({ lines }: { lines: DiffLine[] }) {
  if (lines.length === 0) {
    return <div className="text-sm text-muted-foreground">(empty section)</div>;
  }
  return (
    <pre className="overflow-x-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-5">
      {lines.map((line, i) => (
        <div key={i} className={cn('px-2', toneClass(line.tone))}>
          <span className="select-none text-muted-foreground/70">{toneMarker(line.tone)}</span>
          {line.text}
        </div>
      ))}
    </pre>
  );
}

function toneClass(tone: DiffLine['tone']): string {
  switch (tone) {
    case 'added':
      return 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-300';
    case 'removed':
      return 'bg-red-500/10 text-red-900 dark:text-red-300';
    case 'cosmetic-removed':
      return 'bg-yellow-400/15 text-yellow-800 dark:text-yellow-200';
    case 'cosmetic-added':
      return 'bg-orange-400/20 text-orange-800 dark:text-orange-200';
    default:
      return 'text-foreground';
  }
}

function toneMarker(tone: DiffLine['tone']): string {
  switch (tone) {
    case 'added':
      return '+ ';
    case 'removed':
      return '− ';
    case 'cosmetic-added':
      return '~+';
    case 'cosmetic-removed':
      return '~−';
    default:
      return '  ';
  }
}

// ─── Diff computation ───────────────────────────────────────────────

interface DiffLine {
  tone: 'added' | 'removed' | 'same' | 'cosmetic-added' | 'cosmetic-removed';
  text: string;
}

interface SectionDiff {
  id: string;
  heading: string;
  status: SectionStatus;
  lines: DiffLine[];
}

function buildSectionDiffs(v1: WCRulebook, v2: WCRulebook): SectionDiff[] {
  // Maintain section ordering from v2 (the canonical current view).
  // Sections only present in v1 are appended at the end as "removed".
  const v1ById = new Map<string, WCRulebookSection>();
  for (const s of v1.sections) v1ById.set(s.id, s);
  const v2Ids = new Set(v2.sections.map((s) => s.id));

  const out: SectionDiff[] = [];

  for (const v2Section of v2.sections) {
    const v1Section = v1ById.get(v2Section.id);
    if (!v1Section) {
      out.push({
        id: v2Section.id,
        heading: v2Section.heading,
        status: 'added',
        lines: linesForSection(v2Section).map((t) => ({ tone: 'added', text: t })),
      });
      continue;
    }

    const lines = diffSection(v1Section, v2Section);
    const hasReal = lines.some((l) => l.tone === 'added' || l.tone === 'removed');
    const hasCosmetic = lines.some(
      (l) => l.tone === 'cosmetic-added' || l.tone === 'cosmetic-removed',
    );
    out.push({
      id: v2Section.id,
      heading: v2Section.heading,
      status: hasReal ? 'changed' : hasCosmetic ? 'cosmetic' : 'same',
      lines,
    });
  }

  for (const v1Section of v1.sections) {
    if (v2Ids.has(v1Section.id)) continue;
    out.push({
      id: v1Section.id,
      heading: v1Section.heading,
      status: 'removed',
      lines: linesForSection(v1Section).map((t) => ({ tone: 'removed', text: t })),
    });
  }

  return out;
}

// ─── Section line formatters ────────────────────────────────────────

function formatRound(r: { code: string; name: string; dates: string; teams: number }): string {
  return `${r.code}  ${r.name}  ${r.dates}  ${r.teams} teams`;
}

function formatRow(r: { label: string; value: string }, widest: number): string {
  return `${r.label.padEnd(widest)}  ${r.value}`;
}

/** Whole-section flattener — used only for "section is wholly added/removed" rendering. */
function linesForSection(section: WCRulebookSection): string[] {
  const out: string[] = [];
  if (section.kind === 'rounds') {
    for (const r of section.rows) out.push(formatRound(r));
  } else if (section.kind === 'rows') {
    const widest = Math.max(0, ...section.rows.map((r) => r.label.length));
    for (const r of section.rows) out.push(formatRow(r, widest));
  } else if (section.kind === 'bullets') {
    for (const b of section.bullets) out.push(`• ${b}`);
  } else if (section.kind === 'superpowers') {
    for (const c of section.cards) {
      out.push(`${c.name}  (${c.quota})`);
      out.push(`    ${c.description}`);
    }
  }
  if (section.footer) out.push(`— ${section.footer}`);
  return out;
}

// ─── Kind-aware section diff ────────────────────────────────────────

/**
 * Pair rows by their natural key (round.code / row.label / superpower.name) so
 * each pair renders interleaved as `removed` immediately followed by `added`.
 * Mismatched-kind sections fall back to bullet-style line diffing.
 */
function diffSection(v1: WCRulebookSection, v2: WCRulebookSection): DiffLine[] {
  if (v1.kind === 'rounds' && v2.kind === 'rounds') {
    return diffByKey(
      v1.rows,
      v2.rows,
      (r) => r.code,
      (r) => formatRound(r),
      v1.footer,
      v2.footer,
    );
  }
  if (v1.kind === 'rows' && v2.kind === 'rows') {
    const widest = Math.max(
      0,
      ...v1.rows.map((r) => r.label.length),
      ...v2.rows.map((r) => r.label.length),
    );
    return diffByKey(
      v1.rows,
      v2.rows,
      (r) => normalizeKey(r.label),
      (r) => formatRow(r, widest),
      v1.footer,
      v2.footer,
    );
  }
  if (v1.kind === 'superpowers' && v2.kind === 'superpowers') {
    return diffSuperpowers(v1.cards, v2.cards, v1.footer, v2.footer);
  }
  // bullets, or mismatched kinds — fall back to positional pairing.
  return diffBulletStyle(linesForSection(v1), linesForSection(v2));
}

/**
 * Generic key-based row diff. Pairs each v2 row with the v1 row of the same key
 * (label / code / name). Same-key pairs with identical content emit a single
 * `same` line; differing pairs emit a `removed` immediately followed by an
 * `added` (or both as `cosmetic-*` if the rule is unchanged). v2-only keys
 * become `added`; v1-only keys are appended at the end as `removed`.
 */
function diffByKey<T>(
  v1Rows: T[],
  v2Rows: T[],
  key: (r: T) => string,
  format: (r: T) => string,
  v1Footer: string | undefined,
  v2Footer: string | undefined,
): DiffLine[] {
  const v1ByKey = new Map<string, T>();
  for (const r of v1Rows) v1ByKey.set(key(r), r);

  const out: DiffLine[] = [];
  const seen = new Set<string>();
  for (const v2Row of v2Rows) {
    const k = key(v2Row);
    seen.add(k);
    const v1Row = v1ByKey.get(k);
    const v2Text = format(v2Row);
    if (!v1Row) {
      out.push({ tone: 'added', text: v2Text });
      continue;
    }
    const v1Text = format(v1Row);
    if (v1Text === v2Text) {
      out.push({ tone: 'same', text: v2Text });
      continue;
    }
    pushPair(out, v1Text, v2Text);
  }
  for (const v1Row of v1Rows) {
    const k = key(v1Row);
    if (seen.has(k)) continue;
    out.push({ tone: 'removed', text: format(v1Row) });
  }
  appendFooterDiff(out, v1Footer, v2Footer);
  return out;
}

function diffSuperpowers(
  v1: ReadonlyArray<{ name: string; quota: string; description: string }>,
  v2: ReadonlyArray<{ name: string; quota: string; description: string }>,
  v1Footer: string | undefined,
  v2Footer: string | undefined,
): DiffLine[] {
  const v1ByKey = new Map(v1.map((c) => [normalizeKey(c.name), c]));
  const out: DiffLine[] = [];
  const seen = new Set<string>();
  for (const v2Card of v2) {
    const k = normalizeKey(v2Card.name);
    seen.add(k);
    const v1Card = v1ByKey.get(k);
    const v2Title = `${v2Card.name}  (${v2Card.quota})`;
    const v2Body = `    ${v2Card.description}`;
    if (!v1Card) {
      out.push({ tone: 'added', text: v2Title });
      out.push({ tone: 'added', text: v2Body });
      continue;
    }
    const v1Title = `${v1Card.name}  (${v1Card.quota})`;
    const v1Body = `    ${v1Card.description}`;
    if (v1Title === v2Title) out.push({ tone: 'same', text: v2Title });
    else pushPair(out, v1Title, v2Title);
    if (v1Body === v2Body) out.push({ tone: 'same', text: v2Body });
    else pushPair(out, v1Body, v2Body);
  }
  for (const v1Card of v1) {
    if (seen.has(normalizeKey(v1Card.name))) continue;
    out.push({ tone: 'removed', text: `${v1Card.name}  (${v1Card.quota})` });
    out.push({ tone: 'removed', text: `    ${v1Card.description}` });
  }
  appendFooterDiff(out, v1Footer, v2Footer);
  return out;
}

/** Bullets / mixed-kind: jsdiff line diff, then pair positionally within each
 *  removed/added hunk so each pair is interleaved (removed line immediately
 *  followed by its added counterpart) and tagged cosmetic where applicable. */
function diffBulletStyle(a: string[], b: string[]): DiffLine[] {
  const aText = a.join('\n');
  const bText = b.join('\n');
  const parts = diffLines(aText, bText, { newlineIsToken: false });
  const flat: DiffLine[] = [];
  for (const part of parts) {
    const tone: DiffLine['tone'] = part.added ? 'added' : part.removed ? 'removed' : 'same';
    const split = part.value.split('\n');
    const trimmed = split[split.length - 1] === '' ? split.slice(0, -1) : split;
    for (const l of trimmed) flat.push({ tone, text: l });
  }
  // Walk hunks: for each [removed_run, added_run], pair positionally if alignable.
  const out: DiffLine[] = [];
  let i = 0;
  while (i < flat.length) {
    if (flat[i].tone !== 'removed') {
      out.push(flat[i]);
      i++;
      continue;
    }
    const rStart = i;
    while (i < flat.length && flat[i].tone === 'removed') i++;
    const rEnd = i;
    const aStart = i;
    while (i < flat.length && flat[i].tone === 'added') i++;
    const aEnd = i;
    const removed = flat.slice(rStart, rEnd);
    const added = flat.slice(aStart, aEnd);
    const pairs = Math.min(removed.length, added.length);
    if (pairs > 0 && isPositionallyAlignable(removed, added, pairs)) {
      for (let k = 0; k < pairs; k++) pushPair(out, removed[k].text, added[k].text);
      for (let k = pairs; k < removed.length; k++) out.push(removed[k]);
      for (let k = pairs; k < added.length; k++) out.push(added[k]);
    } else {
      out.push(...removed);
      out.push(...added);
    }
  }
  return out;
}

function isPositionallyAlignable(removed: DiffLine[], added: DiffLine[], pairs: number): boolean {
  if (pairs === 0) return false;
  let aligned = 0;
  for (let k = 0; k < pairs; k++) {
    if (jaccardWords(removed[k].text, added[k].text) >= 0.2) aligned++;
  }
  return aligned / pairs >= 0.5;
}

function pushPair(out: DiffLine[], v1Text: string, v2Text: string): void {
  if (isCosmetic(v1Text, v2Text)) {
    out.push({ tone: 'cosmetic-removed', text: v1Text });
    out.push({ tone: 'cosmetic-added', text: v2Text });
  } else {
    out.push({ tone: 'removed', text: v1Text });
    out.push({ tone: 'added', text: v2Text });
  }
}

function appendFooterDiff(
  out: DiffLine[],
  v1Footer: string | undefined,
  v2Footer: string | undefined,
): void {
  if (!v1Footer && !v2Footer) return;
  if (v1Footer && !v2Footer) {
    out.push({ tone: 'removed', text: `— ${v1Footer}` });
    return;
  }
  if (!v1Footer && v2Footer) {
    out.push({ tone: 'added', text: `— ${v2Footer}` });
    return;
  }
  if (v1Footer === v2Footer) {
    out.push({ tone: 'same', text: `— ${v2Footer}` });
    return;
  }
  pushPair(out, `— ${v1Footer}`, `— ${v2Footer}`);
}

function normalizeKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[—–−]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function isCosmetic(a: string, b: string): boolean {
  const ta = extractValueTokens(a);
  const tb = extractValueTokens(b);
  // Numeric/value tokens must match exactly (order included).
  if (ta.length !== tb.length) return false;
  for (let k = 0; k < ta.length; k++) if (ta[k] !== tb[k]) return false;
  // No numeric tokens (pure prose) → require some word overlap.
  if (ta.length === 0) return jaccardWords(a, b) >= 0.4;
  // Numbers match → also require enough shared words to guard against false
  // positives on lines that share a "+3" but talk about different rules.
  return jaccardWords(a, b) >= 0.25;
}

const VALUE_TOKEN_RE = /[+−-]?\d+(?:\.\d+)?|∞|unlimited/gi;

function extractValueTokens(s: string): string[] {
  const m = s.match(VALUE_TOKEN_RE) ?? [];
  return m
    .map((x) => x.toLowerCase().replace('−', '-'))
    .filter((tok) => {
      // Filter out 4-digit years (1900–2099). Adding ", 2026" to a date string
      // is cosmetic, not a rule change — the underlying rule (the date) is the
      // same. Other numbers in this range are vanishingly rare in a rulebook.
      const n = Number(tok);
      if (Number.isFinite(n) && Number.isInteger(n) && n >= 1900 && n <= 2099) return false;
      return true;
    });
}

function jaccardWords(a: string, b: string): number {
  const wa = wordSet(a);
  const wb = wordSet(b);
  if (wa.size === 0 && wb.size === 0) return 1;
  let inter = 0;
  for (const w of wa) if (wb.has(w)) inter++;
  const union = wa.size + wb.size - inter;
  return union === 0 ? 1 : inter / union;
}

function wordSet(s: string): Set<string> {
  return new Set(
    s
      .toLowerCase()
      .replace(/[—–−]/g, ' ')
      .split(/\W+/u)
      .filter((w) => w.length > 2),
  );
}

// ─── Stats ──────────────────────────────────────────────────────────

interface DiffStats {
  total: number;
  same: number;
  cosmetic: number;
  changed: number;
  added: number;
  removed: number;
  linesAdded: number;
  linesRemoved: number;
  linesCosmetic: number;
}

function summarise(diffs: SectionDiff[]): DiffStats {
  const stats: DiffStats = {
    total: diffs.length,
    same: 0,
    cosmetic: 0,
    changed: 0,
    added: 0,
    removed: 0,
    linesAdded: 0,
    linesRemoved: 0,
    linesCosmetic: 0,
  };
  for (const d of diffs) {
    stats[d.status] += 1;
    for (const line of d.lines) {
      if (line.tone === 'added') stats.linesAdded += 1;
      else if (line.tone === 'removed') stats.linesRemoved += 1;
      else if (line.tone === 'cosmetic-added' || line.tone === 'cosmetic-removed')
        stats.linesCosmetic += 1;
    }
  }
  return stats;
}
